import React, { useEffect, useState } from "react";

import Avatar from "@material-ui/core/Avatar";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import PeopleAltRoundedIcon from "@material-ui/icons/PeopleAltRounded";

import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import { useSetup } from "../hooks/useSetup";

import {
  KoraxOnboardingShell,
  KoraxOnboardingContent,
  KoraxOnboardingFooter
} from "../../../components/KoraxUI/Onboarding";

const useStyles = makeStyles(theme => ({
  panel: {
    width: "100%",
    maxWidth: 760,
    padding: 22,
    boxSizing: "border-box",
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.025)"
        : "#FFFFFF"
  },

  list: {
    display: "grid",
    gridTemplateColumns: "repeat(2,minmax(0,1fr))",
    gap: 12,

    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },

  card: {
    padding: 14,
    display: "flex",
    alignItems: "center",
    gap: 12,
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`
  },

  avatar: {
    width: 42,
    height: 42,
    fontWeight: 800,
    background: "linear-gradient(135deg,#155EEF,#123E9C)"
  },

  name: {
    fontSize: 14,
    fontWeight: 800,
    color: theme.palette.text.primary
  },

  role: {
    marginTop: 3,
    fontSize: 12,
    color: theme.palette.text.secondary
  },

  empty: {
    minHeight: 180,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    textAlign: "center",
    color: theme.palette.text.secondary
  },

  emptyIcon: {
    marginBottom: 10,
    fontSize: 38,
    color: "#155EEF"
  }
}));

export default function SchedulingProfessionalsStep() {
  const classes = useStyles();
  const { nextStep, previousStep } = useSetup();

  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadProfessionals() {
      try {
        const { data } = await api.get("/users/list");
        setProfessionals(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    }

    loadProfessionals();
  }, []);

  return (
    <KoraxOnboardingShell step={6} totalSteps={10}>
      <KoraxOnboardingContent
        eyebrow="Equipe responsável"
        title="Confira os profissionais disponíveis"
        description="Esses são os usuários que poderão receber agendamentos. O vínculo com cada serviço foi configurado na etapa anterior."
      >
        <div className={classes.panel}>
          {loading ? (
            <div className={classes.empty}>
              <CircularProgress size={32} />
            </div>
          ) : professionals.length === 0 ? (
            <div className={classes.empty}>
              <PeopleAltRoundedIcon className={classes.emptyIcon} />

              <Typography variant="body2">
                Nenhum profissional foi encontrado.
              </Typography>
            </div>
          ) : (
            <div className={classes.list}>
              {professionals.map(professional => (
                <div className={classes.card} key={professional.id}>
                  <Avatar className={classes.avatar}>
                    {(professional.name || "?").charAt(0).toUpperCase()}
                  </Avatar>

                  <div>
                    <Typography className={classes.name}>
                      {professional.name}
                    </Typography>

                    <Typography className={classes.role}>
                      Profissional disponível para configuração
                    </Typography>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </KoraxOnboardingContent>

      <KoraxOnboardingFooter
        onBack={previousStep}
        onNext={nextStep}
        nextLabel="Configurar horários"
      />
    </KoraxOnboardingShell>
  );
}
